import React, { useState, useEffect } from "react";
import Select from "react-select";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { apiRequest } from "../../apiServices/allApi";

const Notification = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [sendTo, setSendTo] = useState("all");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);

  const [feedbackModal, setFeedbackModal] = useState({
    show: false,
    title: "",
    message: "",
    isSuccess: false,
  });

  // Fetch users when "selected" audience is picked
  useEffect(() => {
    if (sendTo === "selected" && userOptions.length === 0) {
      fetchUserList();
    }
  }, [sendTo]);

  const fetchUserList = async () => {
    setIsFetchingUsers(true);
    const access_token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${access_token}`,
    };

    try {
      const res = await apiRequest(
        `admin/get_User/all?page=0&_limit=200&_q=0&_stype=0&_status=0&_Userstatus=""`,
        "GET",
        null,
        headers,
      );

      const users = res?.data?.admin || [];
      const formatted = users.map((u) => ({
        value: u._id || u.id,
        label: `${u.Name || "User"} (${u.Phone || "No Phone"})`,
      }));
      setUserOptions(formatted);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setIsFetchingUsers(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleReset = () => {
    setTitle("");
    setBody("");
    setImageFile(null);
    setImagePreview(null);
    setSelectedUsers([]);
    setSendTo("all");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !body.trim()) {
      setFeedbackModal({
        show: true,
        title: "Validation Error",
        message: "Please fill in both title and message.",
        isSuccess: false,
      });
      return;
    }

    if (
      sendTo === "selected" &&
      (!selectedUsers || selectedUsers.length === 0)
    ) {
      setFeedbackModal({
        show: true,
        title: "Audience Missing",
        message: "Please select at least one target user.",
        isSuccess: false,
      });
      return;
    }

    setIsLoading(true);
    const access_token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${access_token}`,
    };

    try {
      let payload;
      let reqHeaders = { ...headers };

      if (imageFile) {
        payload = new FormData();
        payload.append("title", title.trim());
        payload.append("message", body.trim());
        payload.append("sendto", sendTo);
        payload.append("image", imageFile);

        if (sendTo === "selected") {
          const userIds = selectedUsers.map((u) => u.value);
          payload.append("sendto_Id", JSON.stringify(userIds));
        }
      } else {
        const userIds = selectedUsers.map((u) => u.value);
        payload = {
          title: title.trim(),
          message: body.trim(),
          sendto: sendTo,
          sendto_Id:
            sendTo === "selected" ? JSON.stringify(userIds) : undefined,
        };
      }

      const res = await apiRequest(
        "admin/send-notification",
        "POST",
        payload,
        reqHeaders,
      );

      if (res?.data?.error) {
        setFeedbackModal({
          show: true,
          title: "Sending Failed",
          message: res.data.error || "Failed to send notification.",
          isSuccess: false,
        });
      } else {
        setFeedbackModal({
          show: true,
          title: "Notification Sent",
          message:
            res?.data?.msg || "Push notification broadcasted successfully!",
          isSuccess: true,
        });
        handleReset();
      }
    } catch (err) {
      console.error(err);
      setFeedbackModal({
        show: true,
        title: "Error",
        message:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to send push notification. Please check network or try again.",
        isSuccess: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const audienceOptions = [
    {
      id: "all",
      label: "All Users",
      icon: "mdi-account-multiple",
      color: "#2563eb",
    },
    {
      id: "active",
      label: "Active Players",
      icon: "mdi-account-check",
      color: "#16a34a",
    },
    {
      id: "verified",
      label: "Verified Users",
      icon: "mdi-shield-check",
      color: "#0891b2",
    },
    {
      id: "Top-player",
      label: "Top Players",
      icon: "mdi-trophy",
      color: "#d97706",
    },
    {
      id: "selected",
      label: "Specific Users",
      icon: "mdi-account-search",
      color: "#7c3aed",
    },
    {
      id: "Blocked",
      label: "Blocked Users",
      icon: "mdi-account-cancel",
      color: "#dc2626",
    },
  ];

  return (
    <div className="py-3">
      {/* Page Title */}
      <div className="mb-4">
        <div className="d-flex align-items-center">
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
              marginRight: "14px",
              boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
            }}
          >
            <i className="mdi mdi-bell-ring"></i>
          </div>
          <div>
            <h3 className="dashboard_heading mb-1 text-dark font-weight-bold">
              Push Notification Broadcast
            </h3>
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Send real-time alerts and engagement announcements to players
              across devices.
            </p>
          </div>
        </div>
      </div>

      <div className="row">
        {/* COMPOSER FORM */}
        <div className="col-lg-9 mb-4">
          <div
            className="card border-0 shadow-sm"
            style={{ borderRadius: "12px" }}
          >
            <div className="card-body p-4 p-md-5">
              <form onSubmit={handleSubmit}>
                {/* 1. Target Audience */}
                <div className="form-group mb-4">
                  <label className="font-weight-bold text-dark mb-2 d-block">
                    1. Target Audience <span className="text-danger">*</span>
                  </label>
                  <div className="row">
                    {audienceOptions.map((opt) => {
                      const isSelected = sendTo === opt.id;
                      return (
                        <div key={opt.id} className="col-sm-6 col-md-4 mb-2">
                          <div
                            onClick={() => setSendTo(opt.id)}
                            style={{
                              border: isSelected
                                ? `2px solid ${opt.color}`
                                : "1px solid #e2e8f0",
                              backgroundColor: isSelected
                                ? "#f8fafc"
                                : "#ffffff",
                              borderRadius: "8px",
                              padding: "10px 12px",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                            className="d-flex align-items-center"
                          >
                            <input
                              type="radio"
                              name="audienceSelect"
                              checked={isSelected}
                              onChange={() => setSendTo(opt.id)}
                              className="mr-2"
                            />
                            <i
                              className={`mdi ${opt.icon} mr-2`}
                              style={{ color: opt.color, fontSize: "18px" }}
                            ></i>
                            <span
                              className="text-dark font-weight-bold"
                              style={{ fontSize: "13px" }}
                            >
                              {opt.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Multi-select for Specific Users */}
                  {sendTo === "selected" && (
                    <div
                      className="mt-3 p-3 bg-light"
                      style={{ borderRadius: "8px" }}
                    >
                      <label
                        className="font-weight-bold text-dark mb-1"
                        style={{ fontSize: "13px" }}
                      >
                        Select Target Users:
                      </label>
                      <Select
                        isMulti
                        isLoading={isFetchingUsers}
                        options={userOptions}
                        value={selectedUsers}
                        onChange={setSelectedUsers}
                        placeholder={
                          isFetchingUsers
                            ? "Loading users..."
                            : "Search and select users..."
                        }
                        className="text-dark"
                      />
                      <small className="text-muted mt-1 d-block">
                        Selected: <strong>{selectedUsers.length}</strong>{" "}
                        players
                      </small>
                    </div>
                  )}
                </div>

                <hr className="my-4" style={{ borderColor: "#f1f5f9" }} />

                {/* 2. Notification Content */}
                <div className="form-group mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="font-weight-bold text-dark m-0">
                      2. Notification Title{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <small className="text-muted">{title.length}/65</small>
                  </div>
                  <input
                    type="text"
                    maxLength={65}
                    className="form-control"
                    placeholder="e.g. 🎉 Weekend Ludo Tournament is LIVE!"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="font-weight-bold text-dark m-0">
                      3. Message Body <span className="text-danger">*</span>
                    </label>
                    <small className="text-muted">{body.length}/240</small>
                  </div>
                  <textarea
                    rows={4}
                    maxLength={240}
                    className="form-control"
                    placeholder="e.g. Join the mega challenge table now and win ₹10,000 in instant cash prizes! Limited spots available."
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    required
                  />
                </div>

                {/* Optional Banner Image */}
                <div className="form-group mb-4">
                  <label className="font-weight-bold text-dark mb-1 d-block">
                    4. Banner Image{" "}
                    <span className="text-muted font-weight-normal">
                      (Optional)
                    </span>
                  </label>
                  {imagePreview ? (
                    <div
                      className="d-flex align-items-center p-2 bg-light"
                      style={{ borderRadius: "8px" }}
                    >
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: "70px",
                          height: "45px",
                          borderRadius: "6px",
                          objectFit: "cover",
                          marginRight: "12px",
                        }}
                      />
                      <div className="flex-grow-1">
                        <small className="text-dark font-weight-bold d-block">
                          {imageFile?.name}
                        </small>
                        <small className="text-muted">
                          {(imageFile?.size / 1024).toFixed(1)} KB
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-danger"
                        onClick={removeImage}
                      >
                        <i className="mdi mdi-close"></i> Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        id="notifImage"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleImageChange}
                      />
                      <button
                        type="button"
                        className="btn border mt-2"
                        onClick={() =>
                          document.getElementById("notifImage").click()
                        }
                      >
                        <i className="mdi mdi-image-plus mr-1"></i> Attach Image
                        / Banner
                      </button>
                    </div>
                  )}
                </div>

                <hr className="my-4" style={{ borderColor: "#f1f5f9" }} />

                {/* Actions */}
                <div className="d-flex justify-content-end align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary mr-2"
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    Clear
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      "Broadcasting..."
                    ) : (
                      <>
                        <i className="mdi mdi-send mr-1"></i> Send Notification
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* FEEDBACK MODAL */}
      <Modal
        show={feedbackModal.show}
        onHide={() => setFeedbackModal({ ...feedbackModal, show: false })}
        centered
        size="sm"
      >
        <Modal.Header closeButton style={{ borderBottom: "1px solid #e2e8f0" }}>
          <Modal.Title
            className={`h6 font-weight-bold m-0 ${
              feedbackModal.isSuccess ? "text-success" : "text-danger"
            }`}
          >
            <i
              className={`mdi ${
                feedbackModal.isSuccess
                  ? "mdi-check-circle"
                  : "mdi-alert-circle"
              } mr-1`}
            ></i>
            {feedbackModal.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4 bg-white">
          <i
            className={`mdi ${
              feedbackModal.isSuccess
                ? "mdi-check-circle-outline text-success"
                : "mdi-alert-circle-outline text-danger"
            } mb-2 d-block`}
            style={{ fontSize: "44px" }}
          ></i>
          <p className="mb-0 text-dark font-weight-medium">
            {feedbackModal.message}
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center p-2 bg-light">
          <Button
            variant={feedbackModal.isSuccess ? "success" : "danger"}
            size="sm"
            className="px-4"
            onClick={() => setFeedbackModal({ ...feedbackModal, show: false })}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Notification;
